import { type ReactNode } from 'react';

import styles from './ListState.module.css';

/**
 * ListState — the box all three message states are cut from.
 *
 * Handoff, "Empty, loading, error": "Every list has four states, all bordered
 * --color-neutral-800, radius md, min-height 190px, content flush to the inline
 * start." Four identical boxes written four times is four chances for one of
 * them to drift, which is the same argument that produced `Field` in task 21.
 * Loading is the fourth state and is deliberately NOT one of these: skeleton
 * rows live inside the table body at the real row height, not in a box.
 *
 * Three things this component owns rather than its callers:
 *
 * 1. **The icon is decorative.** It repeats what the title says. Announced, it
 *    would read as an unnamed image in front of the sentence that matters.
 * 2. **The title is a <p>, not a heading.** A reusable component cannot know
 *    which heading level it is nested under, and an <h3> that lands under an
 *    <h1> is a broken document outline. The state reaches assistive technology
 *    through DataTable's live region and, for the error, through `role="alert"`
 *    — neither of which needs a heading.
 * 3. **`tone` is the only visual switch.** The error tone takes the escalated
 *    hue on the border and the icon, per the handoff.
 */

export interface ListStateProps {
  tone?: 'neutral' | 'error';
  icon: ReactNode;
  title: string;
  /** One or more explanatory lines. Rendered in order, all as body copy. */
  body: ReactNode;
  /** Handoff: the action is pinned to the bottom of the box. */
  action?: ReactNode;
  /**
   * Set on the error state only. `role="alert"` announces on insertion, which
   * is what makes a failure audible at the moment it happens rather than the
   * next time focus wanders past it.
   */
  role?: 'alert';
}

export function ListState({ tone = 'neutral', icon, title, body, action, role }: ListStateProps) {
  return (
    <div
      className={[styles['state'], tone === 'error' ? styles['error'] : null]
        .filter(Boolean)
        .join(' ')}
      data-testid="listState"
      data-tone={tone}
      {...(role === undefined ? {} : { role })}
    >
      <span className={styles['icon']} aria-hidden="true" data-testid="listStateIcon">
        {icon}
      </span>
      <p className={styles['title']}>{title}</p>
      <div className={styles['body']}>{body}</div>
      {action === undefined ? null : <div className={styles['action']}>{action}</div>}
    </div>
  );
}
